/**
 * @packageDocumentation
 * Bootstraps the Superhero Incognito experience by initialising PixiJS, wiring the preloader, loading assets, building UI layers, and orchestrating the main card screen.
 */
import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { Assets } from "pixi.js";
import { StartAppResize } from "./resizer";
import { betArea } from "./Game/betArea";
// import * as PIXI3D from "pixi3d"
import { currencySymbol, ShowCent } from "./Game/server";
// import {instantWin} from "./Game/instantWin";
import { gsap } from "gsap/gsap-core";
import { BottomBar } from "./Game/bottomBar";
import { PixiPlugin } from "gsap/all";
import { UserOptions } from "./Game/settings";
gsap.registerPlugin(PixiPlugin);
PixiPlugin.registerPIXI(PIXI);
import { BMSwinProperties, GridProperties, HistoryProperties } from "./config";
import { canvasProperties } from "./config";
import { createStartPage } from "./Game/startPage";
import { createEndPage } from "./Game/endPage";
// import { initDevtools } from '@pixi/devtools';
import { initDevtools } from "@pixi/devtools";

import {
  applyCent,
  brightnessFilter,
  disabledfilter,
  font_main,
  shockwave,
} from "./Game/global";
import { Button } from "./lib/buttonCreator";
import { SoundManager } from "./Game/sounds";
import { backgroundAnimations } from "./Game/background";
import { electricShake } from "./lib/shakeEffect";
import { GameHistory } from "./Game/history";
import { Preloader } from "./preloader";
import { CardContainer, CardScreen, onSpinClicked } from "./Game/CardScreen";
import { specsFromRoundTuple } from "./Game/loadCards";
import { TotalWinButton } from "./Game/totalWinButton";
import { InfoButton } from "./Game/InfoButton";
import { PrizeChartButton, PrizeChartPopup } from "./Game/prizeChartButton";
import { WinPopup } from "./Game/winpopup";
import { AutoplayManager } from "./Game/Autoplay";
import { TestCasePanel } from "./Game/TestCasePanel";
import bottomWin from "./Game/bottomWin";
import { Bonus } from "./Game/Bonus";

export let LogoAnim!: Spine & {
  winPopup?: WinPopup;
  // totalwinContainer?:TotalWinButton
  StartBut_enableFun?: () => void;
  StartBut_disableFun?: () => void;
  totalwinContainer?: TotalWinButton;
  bottomWin?: bottomWin;
  allsuper?: boolean;
  insideBonus?: boolean;
};
export const overlay = document.createElement("div");

export function clearFigmaBaseScaleCache(root: any) {
  const walk = (obj: any) => {
    if (!obj) return;

    // Remove cached base scale so next layout captures correct "author" scale
    if (obj.__figmaBaseScale) delete obj.__figmaBaseScale;

    // Recurse children if it's a container
    if (obj.children && Array.isArray(obj.children)) {
      obj.children.forEach(walk);
    }
  };

  walk(root);
}

/**
 * Main application file for the game
 * @module app
 */

/**
 * Canvas width from properties
 * @constant {number}
 */
const W = canvasProperties.width;

/**
 * Canvas height from properties
 * @constant {number}
 */
const H = canvasProperties.height;

/**
 * Whether to resize canvas to device size
 * @constant {boolean}
 */
const resize_to_device = canvasProperties.resize;

/**
 * Color matrix filter for visual effects
 * @constant {PIXI.ColorMatrixFilter}
 */
const filter = new PIXI.ColorMatrixFilter();

/**
 * Secondary color matrix filter
 * @constant {PIXI.ColorMatrixFilter}
 */
const filter1 = new PIXI.ColorMatrixFilter();



/**
 * Total number of spins allowed
 * @type {number}
 */
let totalSpinCount = 6;

/**
 * Player's bet amount
 * @type {number}
 */
let playerBet = 2;

/**
 * Current round count
 * @type {number}
 */
let RoundCount = 0;

/**
 * Timeout for BMS win animation
 * @type {NodeJS.Timeout}
 */
let BME_TimeOut: NodeJS.Timeout;

/**
 * Function to adjust layout for portrait mode
 * @type {()=>void}
 */
let PortraitAdjustFun: () => void;

/**
 * Function to adjust layout for landscape mode
 * @type {()=>void}
 */
let landScapeAdjustFun: () => void;

/**
 * Function to adjust preloader for landscape mode
 * @type {()=>void}
 */
let landscapeAdjust_preloader: () => void;

/**
 * Function to adjust preloader for portrait mode
 * @type {()=>void}
 */
let PortraitAdjust_preloader: () => void;

/**
 * Whether BMS win is currently triggered
 * @type {boolean}
 */
let BMS_win_triggered = false;

/**
 * Main application initialization function
 * @async
 * @function app_main
 */

async function app_main() {
  /**
   * Creates the background stage for animations
   * @param app_width - Width of the application canvas
   * @param app_height - Height of the application canvas
   * @param resize_to_device - Whether to resize canvas to device size
   */
  const BgStage = new backgroundAnimations(
    W,
    H,
    resize_to_device
  );

  // ===== FIGMA SOURCE OF TRUTH (Landscape) =====
  const FIGMA = {
    BASE_W: 1921,
    BASE_H: 1081,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    GAMEBOARD: { left: 230, top: 53, w: 1460, h: 975, scale: 1 },
    LOGO: { left: 783, top: 9, w: 355, h: 230, scale: 1 },
    HAMBURG: { left: 1755, top: 59, w: 66, h: 66, scale: 1 },

    HOW2PLAY: { left: 330, top: 104.25, w: 306.25, h: 90, scale: 1 },
    PRIZECHARTS: { left: 1316.25, top: 104.25, w: 316.25, h: 90, scale: 1 },
    TOTALWIN: { left: 462, top: 851, w: 242, h: 132, scale: 1 },
    BETAMOUNT: { left: 1238, top: 874, w: 242, h: 132, scale: 1 },
    PLAY: { left: 882, top: 815, w: 158, h: 159, scale: 1 },
    AUTOPLAY: { left: 796, top: 905, w: 76, h: 76, scale: 1 },

    CARD_LEFT: { left: 418.51, top: 263, w: 350, h: 539.48 },
    CARD_CENTER: { left: 796, top: 263, w: 350, h: 539.48 },
    CARD_RIGHT: { left: 1180, top: 263, w: 350, h: 539.48 },

    BOTTOMWIN: { left: 426, top: 804, w: 1067.19, h: 175.79, scale: 1 },

  } as const;

  const FIGMA_P = {
    BASE_W: 1080,
    BASE_H: 1920,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    LOGO: { left: 304, top: 217, w: 412, h: 267, scale: 1 },
    GAMEBOARD: { left: 12, top: 494, w: 1048, h: 700, scale: 1 },
    HAMBURG: { left: 896, top: 213, w: 66, h: 66, scale: 1 },

    HOW2PLAY: { left: 119, top: 529, w: 245, h: 72, scale: 1 },
    PRIZECHARTS: { left: 709, top: 529, w: 253, h: 72, scale: 1 },

    TOTALWIN: { left: 113, top: 1239, w: 284, h: 155, scale: 1 },
    BETAMOUNT: { left: 579.68, top: 1266, w: 283.94, h: 155.14, scale: 1 },

    PLAY: { left: 400, top: 1435, w: 267, h: 269, scale: 1.2 },
    AUTOPLAY: { left: 667, top: 1586, w: 128, h: 128, scale: 1 },

    CARD_LEFT: { left: 127, top: 636, w: 265, h: 404 },
    CARD_CENTER: { left: 406, top: 636, w: 266, h: 404 },
    CARD_RIGHT: { left: 691, top: 636, w: 265, h: 404 },

    BOTTOMWIN: { left: 0, top: 1130, w: 1067.19, h: 175.79, scale: 1 },

  } as const;



  // Layout the “figma frame” container to the screen
  function layoutDesignRoot(
    designRoot: PIXI.Container,
    screenW: number,
    screenH: number,
    cfg: { BASE_W: number; BASE_H: number; ROOT_OFFSET_X: number; ROOT_OFFSET_Y: number }
  ) {
    const s = Math.min(screenW / cfg.BASE_W, screenH / cfg.BASE_H);

    designRoot.scale.set(s);
    designRoot.x = (screenW - cfg.BASE_W * s) / 2 + cfg.ROOT_OFFSET_X;
    designRoot.y = (screenH - cfg.BASE_H * s) / 2 + cfg.ROOT_OFFSET_Y;

    return s;
  }


  // Convert figma left/top + w/h into centered (for anchor 0.5)
  function figmaCenter(left: number, top: number, w: number, h: number) {
    return { x: left + w / 2, y: top + h / 2 };
  }

  /**
   * Places ANY display object by Figma left/top/width/height.
   * Uses visual bounds to set pivot correctly (works for Containers too).
   */
  function placeFromFigmaLTWH(
    obj: PIXI.Container,
    left: number,
    top: number,
    w: number,
    h: number,
    opts?: { fitToSize?: boolean; nudgeX?: number; nudgeY?: number }
  ) {
    const { fitToSize = false, nudgeX = 0, nudgeY = 0 } = opts ?? {};

    if (fitToSize) {
      const anyObj = obj as any;

      if (!anyObj.__figmaBaseScale) {
        anyObj.__figmaBaseScale = { x: obj.scale.x, y: obj.scale.y };
      }

      const base = anyObj.__figmaBaseScale as { x: number; y: number };

      // ✅ Prefer stable boundsArea if present
      const ba = (obj as any).boundsArea as PIXI.Rectangle | undefined;
      const lb = ba ?? obj.getLocalBounds();
      const bw = Math.max(1, lb.width);
      const bh = Math.max(1, lb.height);

      const s = Math.min(w / bw, h / bh);
      obj.scale.set(base.x * s, base.y * s);
    }

    // ✅ Pivot from stable bounds as well (DO NOT override this later)
    const ba2 = (obj as any).boundsArea as PIXI.Rectangle | undefined;
    const b = ba2 ?? obj.getLocalBounds();
    obj.pivot.set(b.x + b.width / 2, b.y + b.height / 2);

    const c = figmaCenter(left, top, w, h);
    obj.position.set(c.x + nudgeX, c.y + nudgeY);
  }






  function boundsFromSlots(slots: Array<{ left: number; top: number; w: number; h: number }>) {
    const minL = Math.min(...slots.map(s => s.left));
    const minT = Math.min(...slots.map(s => s.top));
    const maxR = Math.max(...slots.map(s => s.left + s.w));
    const maxB = Math.max(...slots.map(s => s.top + s.h));
    const w = maxR - minL;
    const h = maxB - minT;
    return { left: minL, top: minT, w, h, cx: minL + w / 2, cy: minT + h / 2 };
  }

  /**
   * Scales + centers a container so its visual bounds fit inside a target LTWH region.
   * (Perfect for your 3-card group.)
   */
  function fitContainerToFigmaLTWH(
    container: PIXI.Container,
    target: { left: number; top: number; w: number; h: number },
    opts?: { cover?: boolean; nudgeX?: number; nudgeY?: number }
  ) {
    const { cover = false, nudgeX = 0, nudgeY = 0 } = opts ?? {};

    // ✅ Use stable bounds if provided (prevents shrink when front Spine expands bounds)
    const ba = (container as any).boundsArea as PIXI.Rectangle | undefined;
    const b = ba ?? container.getLocalBounds();

    if (b.width < 1 || b.height < 1) return false;

    const sx = target.w / b.width;
    const sy = target.h / b.height;
    const s = cover ? Math.max(sx, sy) : Math.min(sx, sy);

    container.scale.set(s);
    container.pivot.set(b.x + b.width / 2, b.y + b.height / 2);
    container.position.set(
      target.left + target.w / 2 + nudgeX,
      target.top + target.h / 2 + nudgeY
    );

    return true;
  }

  /**
 * Refit the 3-card group to a target slot rect.
 * Runs twice (now + next frame) to avoid stale bounds after orientation changes.
 */

  function refitCardsToSlot(
    cardscreen: any,
    slotRect: { left: number; top: number; w: number; h: number }
  ) {
    if (!cardscreen?.cardContainer) return;

    const cc: PIXI.Container = cardscreen.cardContainer;
    console.log("REFIT target =", cc === cardscreen.cardContainer, cc.children.length, cc.getLocalBounds());


    // Reset any orientation residue (prevents drift across portrait <-> landscape)
    cc.rotation = 0;
    cc.skew.set(0, 0);
    cc.scale.set(1);
    cc.position.set(0, 0);

    const doFit = () => fitContainerToFigmaLTWH(cc, slotRect, { cover: false });

    // Fit now; if bounds aren't ready, try next frame
    const okNow = doFit();

    // Always fit again next frame (Pixi transforms settle after designRoot scaling)
    requestAnimationFrame(() => {
      doFit();
    });

    return okNow;
  }



  /**
   * Main PIXI application instance
   * @type {PIXI.Application}
   */
  // <div id="overlay"></div>
  overlay.id = "overlay";
  overlay.style.zIndex = "1";
  overlay.style.display = "none";
  document.body.appendChild(overlay);

  const app: PIXI.Application = new PIXI.Application();

  /**
   * Initialize the PIXI application with configuration
   * @param width - Canvas width
   * @param height - Canvas height
   * @param backgroundAlpha - Background transparency (0 = transparent)
   * @param resolution - Canvas resolution multiplier
   */
  await app.init({
    width: W,
    height: H,
    backgroundAlpha: 0,
    resolution: 2,
  });
  document.body.appendChild(app.canvas);
  app.renderer.canvas.style.zIndex = "2";
  (globalThis as any).__PIXI_APP__ = app;
  // (globalThis as any).__PIXI_STAGE__ = app.canvas;
  /**
   * Configure application resize handling
   * @param app - PIXI Application instance
   * @param app_width - Application width
   * @param app_height - Application height
   * @param resize_to_device - Whether to resize to device
   * @param transformOrigin_Y - Transform origin Y position
   * @param translate_Y - Translation Y amount
   * @param app_top - Top position of app
   * @param app_name - Name of application
   */
  StartAppResize({
    app: app,
    app_width: W,
    app_height: H,
    resize_to_device: resize_to_device,
    transformOrigin_Y: 50,
    translate_Y: 50,
    app_top: 50,
    app_name: "app",
  });

  /**
   * Initialize PIXI devtools for debugging
   * @param app - PIXI Application instance
   */
  initDevtools({
    app,
  });

  /**
   * Create and add preloader page
   * @param app_width - Application width
   * @param app_height - Application height
   * @param openGame - Callback function when loading completes
   */
  const preloaderPage = new Preloader(app, W, H, openGame);

  app.stage.addChild(preloaderPage);

  /**
   * Create bottom bar UI component
   * @type {BottomBar}
   */
  let BottomStage = await new BottomBar();
  //....................................................................
  function openGame(this: any) {
    /**
     * Initializes the game by setting up background, sounds and UI elements
     */

    // Play background music and initialize background animations
    // if (preloaderPage.getSoundConsent()) {
    //   SoundManager.bgMusicSndPlay();
    // } else {
    //   SoundManager.MuteBgMusicFun();
    // }
    const getWH = () => ({
      W: app.renderer.screen.width,
      H: app.renderer.screen.height,
    });
    const { W, H } = getWH();

    BgStage.formWideBg();
    BgStage.formBgAnimations();


    // Add bottom stage and remove preloader
    document.body.appendChild(BottomStage.app_bottom.canvas);
    BottomStage.app_bottom.renderer.canvas.style.zIndex = "3";



    app.stage.removeChild(preloaderPage);

    /**
     * Creates the outer frame sprite that contains the electric field animations
     * @type {PIXI.Sprite}
     */
    // Root container representing the Figma frame (single source of truth)
    const designRoot = new PIXI.Container();
    designRoot.sortableChildren = true;
    app.stage.addChild(designRoot);

    // Layers inside the design root
    const boardLayer = new PIXI.Container();
    const uiLayer = new PIXI.Container();

    boardLayer.sortableChildren = true;
    uiLayer.sortableChildren = true;

    boardLayer.zIndex = 0;
    uiLayer.zIndex = 10;

    designRoot.addChild(boardLayer);
    designRoot.addChild(uiLayer);




    // Combined GameBoard sprite replacing reel_bg and reelbg_1
    // Combined GameBoard sprite replacing reel_bg and reelbg_1
    let gameBoard = new PIXI.Sprite(Assets.get("GameBoard"));
    gameBoard.anchor.set(0.5);
    gameBoard.alpha = 1;

    boardLayer.addChild(gameBoard);
    gameBoard.zIndex = 0;




    /**
     * Creates the start page instance and sets initial alpha
     */
    const startPage = new createStartPage();
    startPage.alpha = 0;

    /**
     * Creates and configures the start button
     * @param text - Button text "PLAY"
     * @param fontFamily - Font family to use
     * @param fontSize - Font size in pixels
     */
    const StartBut = new Button("Spin_but", {
      text: "",
      fontFamily: font_main,
      fontSize: 40,
    });
    StartBut.setTextPosition(0, -3);
    StartBut.x = 686;
    StartBut.y = 670;
    StartBut.scale.set(1);
    uiLayer.addChild(StartBut);

    /**
     * Start button click handler function
     * Initializes game state and transitions to gameplay
     * @async
     */
    const startFun = async () => {
      console.log("testing ", this.cardscreen);
      if (this.cardscreen && this.cardscreen.screen) {
        LogoAnim.StartBut_disableFun!();
        onSpinClicked(this.cardscreen.screen);
      }

      // Play button click sound and enable gauge sounds
      SoundManager.Btnclick_1SndPlay();

      // Configure button and grid states
      // StartBut.btnGlowAnim.state.timeScale = 0;

      // StartBut.visible = false

      // Configure bet area
      BetAreaContainer.gameStarted = true;
      BetAreaContainer.visible = true;
      gsap.to(BetAreaContainer.plusContainer, { duration: 2, x: 0 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 2, x: 0 });

      // Update bet display and balance
      BetAreaContainer.betval.text =
        "" + currencySymbol + BottomBar.TotalBetvalue.toFixed(2);
      applyCent(BetAreaContainer.betval, BottomBar.TotalBetvalue);
      BottomBar.TotalBalancevalue =
        BottomBar.TotalBalancevalue - BottomBar.TotalBetvalue;
      BottomBar.updateBalance(BottomBar.TotalBalancevalue);

      // Set initial spin count and show grid
      // SpinButton.spinCountNumber.text = 6;
      // startPage.visible = false;
      this.cardscreen.visible = true;
    };

    /**
     * Configure start button glow animation and interaction
     */
    StartBut.formBtnGlow({
      skeleton: "start_btn_anim_json",
      atlas: "start_btn_anim_atlas",
      scale: 1,
    });
    StartBut.addInteraction(startFun);
    StartBut.interactive = false;
    StartBut.btnGlowAnim.state.setAnimation(0, "animation", true);
    StartBut.btnGlowAnim.state.timeScale = 0;
    StartBut.btnGlowAnim.visible = false;

    gsap.to(StartBut, { duration: 0, pixi: { brightness: 0.5 } });

    // /**
    //  * Creates and configures the main game grid
    //  */
    // const grid = new Grid(GridProperties.TotalRows, GridProperties.TotalColumns);
    // grid.visible = true;

    // grid.pivot.set(startPage.width/2-GridProperties.boxWidth/2,startPage.height/2-GridProperties.boxHeight/2);
    // app.stage.addChild(grid);

    this.cardscreen = new CardContainer(app);
    const cardscreen = this.cardscreen;          // ✅ stable reference (no 'this' needed later)
    const screen = cardscreen.screen;


    // this.cardscreen.screen.visible=false
    // screen?.on("roundstart", () => {
    //   StartBut.interactive = false;


    //   // StartBut.alpha = 0.6;
    // });

    // screen?.on("roundend", () => {
    //   StartBut.interactive = true;
    //   // StartBut.alpha = 1;
    // });
    // LogoAnim.totalwinContainer = totalwinContainer;
    // (optional) read the win and update your UI
    // screen?.on("win", ({ amount }) => {
    //   // example: update Total Win UI if you expose a setter (see below)
    //   totalwinContainer?.setValue?.(amount);
    // });

    // empty popup hook you can fill later anywhere:
    // (screen as any).showWinPopup = () => { /* TODO: open your popup */ };
    // cardscreen.position.set(canvasProperties.width/2, canvasProperties.height/2);

    let pageAssumed_x = 536.28;
    let pageAssumed_y = 426;
    let pageAssumed_px = startPage.width / 2 - GridProperties.boxWidth / 2;
    let pageAssumed_py = startPage.height / 2 - GridProperties.boxHeight / 2;

    /**
     * Configure start page position relative to grid
     */
    startPage.x = pageAssumed_x;
    startPage.y = pageAssumed_y;
    startPage.pivot.set(pageAssumed_px, pageAssumed_py);
    this.cardscreen.app = app;


    setTimeout(() => {
      gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 100 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: -100 });
      StartBut.filters = [filter1];
      gsap.to(StartBut, {
        duration: 0.5,
        pixi: { brightness: 1 },
        onComplete: () => {
          StartBut.filters = [];
          StartBut.btnGlowAnim.state.timeScale = 1;
          StartBut.btnGlowAnim.visible = true;
        },
      });
      console.log("testing StartBut.interactive")
      StartBut.interactive = true;
      gsap.to(startPage, { duration: 0, alpha: 1 });
    }, 10);
    app.stage.addChild(startPage);


    // const autoplay = new AutoplayManager(this.cardscreen.screen, () => onSpinClicked(this.cardscreen.screen));
    const autoplay = new AutoplayManager(() => this.cardscreen.screen);
    // position it next to your Spin or Bet area (tweak for your layout)
    autoplay.position.set(754, 650);
    autoplay.scale.set(0.7);
    autoplay.zIndex = 10;

    // If you have a bottomBar container, add it there; else add to stage/UI layer
    uiLayer.addChild(autoplay);

    // (Optional) stop autoplay when user clicks Spin manually
    StartBut?.on?.("pointertap", () => autoplay.stop());

    // (Optional) expose for debug
    (window as any).autoplay = autoplay;
    const testCasePanel = new TestCasePanel();
    (window as any).testCases = testCasePanel;


    /**
     * Creates and configures the infoButton UI component
     */
    const infoButton = new InfoButton();
    infoButton.pivot.set(infoButton.width / 2, infoButton.height / 2);
    infoButton.x = 473;
    infoButton.y = 102;
    infoButton.visible = true;
    uiLayer.addChild(infoButton);

    /**
 
/**
 * Creates and configures the infoButton UI component
 */
    const prizeChartButton = new PrizeChartButton(app);
    prizeChartButton.pivot.set(
      prizeChartButton.width / 2,
      prizeChartButton.height / 2
    );
    prizeChartButton.x = 1077;
    prizeChartButton.y = 102;
    prizeChartButton.visible = true;
    uiLayer.addChild(prizeChartButton);


    /**





/**
 * Creates and configures the Total bet UI component
 */
    const totalwinContainer = new TotalWinButton();
    totalwinContainer.pivot.set(
      totalwinContainer.width / 2,
      totalwinContainer.height / 2
    );
    totalwinContainer.x = 400;
    totalwinContainer.y = 738;
    totalwinContainer.visible = true;
    uiLayer.addChild(totalwinContainer);

    /**
     * Creates and configures the bet area UI component
     */
    const BetAreaContainer = new betArea();
    BetAreaContainer.pivot.set(
      BetAreaContainer.width / 2,
      BetAreaContainer.height / 2
    );
    BetAreaContainer.x = 1150;
    BetAreaContainer.y = 738;
    BetAreaContainer.visible = true;
    uiLayer.addChild(BetAreaContainer);
    (window as any).betArea = BetAreaContainer;
    boardLayer.addChild(this.cardscreen);
    this.cardscreen.zIndex = 5;

    let wasSpinning = false;

    /**
     * Update loop for spin button state
     */
    app.ticker.add(() => {
      if (!this.cardscreen.spinStarted) {
        if (wasSpinning) {
          wasSpinning = false;
        }
      } else {
        wasSpinning = true;
      }
    });

    /**
     * Handles keyboard space bar press
     * Triggers spin or stop based on visible button
     */
    document.addEventListener("keydown", (keycode) => {
      if (keycode.code == "Space") {
        if (StartBut.visible) {
        }
      }
    });

    /**
     * Creates BMS win animation using Spine
     * @type {Spine}
     */
    const BMS_animation = Spine.from({
      skeleton: "bms_win_anim_json",
      atlas: "bms_win_anim_atlas",
      scale: 0.5,
    });
    BMS_animation.x = W / 2 - 40;
    BMS_animation.y = H / 2 - 38;
    BMS_animation.state.setAnimation(0, "big_win_anim", true);
    BMS_animation.state.timeScale = 0;
    BMS_animation.visible = false;
    this.cardscreen.addChild(BMS_animation);
    this.cardscreen.BMS_animation = BMS_animation;

    /**
     * Configures BMS animation interaction properties
     */
    BMS_animation.interactive = false;
    BMS_animation.cursor = "pointer";

    /**
     * Handles BMS animation click event
     * Stops sounds and transitions to next state
     */
    BMS_animation.on("pointertap", () => {
      gsap.to(
        [
          SoundManager.big_win_snd,
          SoundManager.mega_win_snd,
          SoundManager.super_win_snd,
        ],
        {
          duration: 1,
          volume: 0,
          onComplete: () => {
            SoundManager.big_win_snd.stop();
            SoundManager.big_win_snd.volume(1);
            SoundManager.mega_win_snd.stop();
            SoundManager.mega_win_snd.volume(1);
            SoundManager.super_win_snd.stop();
            SoundManager.super_win_snd.volume(1);
          },
        }
      );
      clearTimeout(BME_TimeOut);
      BMS_animation.state.timeScale = 0;
      BMS_animation.visible = false;
      BMS_win_triggered = false;
      BMS_animation.interactive = false;
      this.cardscreen.BMS_win_triggered = BMS_win_triggered;
      if (!this.cardscreen.isThereMingame) {
        this.cardscreen.spinStarted = false;
      }
    });

    /**
     * Shows BMS animation with specified animation type
     * @param anim - Animation name to play
     * @param win - Win amount
     */
    BottomBar.showBmsAnim = (anim: string, win: number) => {
      console.log("special win opened");
      BMS_win_triggered = true;
      this.cardscreen.BMS_win_triggered = BMS_win_triggered;
      BMS_animation.visible = true;
      BMS_animation.state.setAnimation(0, anim, true);
      BMS_animation.state.timeScale = 1;
      BME_TimeOut = setTimeout(() => {
        gsap.to(
          [
            SoundManager.big_win_snd,
            SoundManager.mega_win_snd,
            SoundManager.super_win_snd,
          ],
          {
            duration: 1,
            volume: 0,
            onComplete: () => {
              SoundManager.big_win_snd.stop();
              SoundManager.big_win_snd.volume(1);
              SoundManager.mega_win_snd.stop();
              SoundManager.mega_win_snd.volume(1);
              SoundManager.super_win_snd.stop();
              SoundManager.super_win_snd.volume(1);
            },
          }
        );

        BMS_animation.interactive = false;
        BMS_animation.state.timeScale = 0;
        const endPage = new createEndPage();
        BMS_animation.visible = false;
        BMS_win_triggered = false;
        if (!this.cardscreen.isThereMingame) {
          this.cardscreen.spinStarted = false;
        }
        this.cardscreen.BMS_win_triggered = BMS_win_triggered;
      }, BMSwinProperties.stayTime);
    }; //.............................................................................
    /**
     * Creates and configures the end page for game completion
     * @type {createEndPage}
     */
    const endPage = new createEndPage();
    endPage.x = pageAssumed_x;
    endPage.y = pageAssumed_y;
    endPage.pivot.set(pageAssumed_px, pageAssumed_py);
    endPage.visible = false;

    /**
     * Handles hiding the end page and resetting game state
     * Resets counters, gauges, animations and UI elements
     */
    endPage.hideEndpage = () => {
      return;
      gsap.killTweensOf(endPage.ContgratulationsTxt);
      gsap.killTweensOf(endPage.YouWonTxt);
      BottomBar.TotalBalancevalue =
        BottomBar.TotalBalancevalue + BottomBar.TotalWinvalue;
      BottomBar.updateBalance(BottomBar.TotalBalancevalue);
      BottomBar.updateTotalWin(0);
      endPage.collectButContainer.visible = false;
      RoundCount = 0;
      BetAreaContainer.gameStarted = false;
      endPage.visible = false;

      // Reset UI states and animations
      this.cardscreen.filters = [filter];
      StartBut.filters = [filter1];
      gsap.to(StartBut, { duration: 0, pixi: { brightness: 0.5 } });
      StartBut.visible = true;
      this.cardscreen.spinStarted = true;
      this.cardscreen.totalWin = 0;

      this.cardscreen.visible = true;
      startPage.visible = true;
      startPage.alpha = 0;

      // Delayed UI updates
      setTimeout(() => {
        gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 95 });
        gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: -95 });
        StartBut.filters = [filter1];
        gsap.to(StartBut, {
          duration: 0.5,
          pixi: { brightness: 1 },
          onComplete: () => {
            StartBut.filters = [];
            StartBut.btnGlowAnim.state.timeScale = 1;
            StartBut.btnGlowAnim.visible = true;
          },
        });
        StartBut.interactive = true;
        gsap.to(startPage, { duration: 0.5, alpha: 1 });
      }, 1000);
    };

    /**
     * Creates and configures the end page transition animation
     * @type {Spine}
     */
    const endPageTransitionAnim = endPage.formEndPagePreAnim(endPage);
    endPageTransitionAnim.x = pageAssumed_x;
    endPageTransitionAnim.y = pageAssumed_y;
    app.stage.addChild(endPageTransitionAnim);
    app.stage.addChild(endPage);
    this.cardscreen.EndPage = endPage;

    /**
     * Shows the end page with transition animations
     */
    this.cardscreen.showEndpage = () => {
      this.cardscreen.isThereMingame = false;
      SoundManager.Final_transitionSndPlay();
      endPageTransitionAnim.state.setAnimation(0, "animation", false);
      endPageTransitionAnim.state.timeScale = 1;
      endPageTransitionAnim.visible = true;
      setTimeout(() => {
        this.cardscreen.visible = false;
      }, 500);
      setTimeout(() => {
        electricShake(gameBoard, 10, 0.05, 10, gameBoard.x, gameBoard.y);
        console.log(this.cardscreen.totalWin, "this.cardscreen.totalWin");
        endPage.UpdateEndWinText(this.cardscreen.totalWin);
      }, 1500);
    };

    /**
     * Creates options/settings button
     * @type {UserOptions}
     */
    const OptionsIcon = new UserOptions();
    uiLayer.addChild(OptionsIcon);


    /**
     * Creates static villain logo (temporarily replacing animated logo)
     * @type {PIXI.Sprite cast as Spine for compatibility}
     */
    const logoSprite = new PIXI.Sprite(Assets.get("startPage_logo"));
    logoSprite.anchor.set(0.5);



    // Cast as Spine type for compatibility with existing code
    LogoAnim = logoSprite as any;
    LogoAnim.name = "Logo_Anim1"
    uiLayer.addChild(LogoAnim);
    LogoAnim.insideBonus = false;

    // Add mock animation methods for compatibility
    (LogoAnim as any).state = {
      setAnimation: () => ({} as any),
      addAnimation: () => ({} as any),
      timeScale: 0.8
    };


    LogoAnim.StartBut_enableFun = () => {
      gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 100 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: -100 });
      StartBut.interactive = true;
      StartBut.filters = []
      autoplay.autoBtn.interactive = true;
      autoplay.autoBtn.filters = []
    };
    LogoAnim.StartBut_disableFun = () => {
      gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 0 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: 0 });
      StartBut.interactive = false;
      StartBut.filters = [disabledfilter]
      autoplay.autoBtn.interactive = false;
      autoplay.autoBtn.filters = [disabledfilter]
    };

    // screen!.LogoAnim = LogoAnim;
    /**
     * Plays win animation for logo
     */
    function playLogoWinAnim() {
      LogoAnim.state.setAnimation(0, "win_anim", false);
      LogoAnim.state.addAnimation(0, "idle_anim", true);
    }
    this.cardscreen.playLogoAnim = playLogoWinAnim;

    /**
     * Orientation state flags
     */
    let portrait = false;
    let Landscape = true;

    const BottomWin = new bottomWin()
    app.stage.addChild(BottomWin);
    BottomWin.x = W / 2;
    BottomWin.y = H / 2 + H / 3 + 30;
    BottomWin.visible = false;
    LogoAnim.bottomWin = BottomWin;
    const Win_Popup = new WinPopup();
    app.stage.addChild(Win_Popup);
    LogoAnim.winPopup = Win_Popup;
    Win_Popup.LogoAnim = LogoAnim;
    LogoAnim.totalwinContainer = totalwinContainer;
    const prizePopup = new PrizeChartPopup(app);
    app.stage.addChild(prizePopup);
    prizeChartButton.prizePopup = prizePopup

    // setTimeout(() => {
    const BonusPage = new Bonus();
    app.stage.addChild(BonusPage)
    BonusPage.app = app;
    BonusPage.bonusBg = BgStage.bgSprite;
    Win_Popup.BonusPage = BonusPage;
    setTimeout(() => {
      // const bonusData = [["power-up",1,4,3,5],[30,"power-up",50],["power-up",100],[1000]] 
      // BonusPage.startBonus(bonusData)

    }, 1000);
    // }, 1000);
    // Win_Popup.showWinpopup(0.3)


    type FigmaSlot = {
      left: number;
      top: number;
      w: number;
      h: number;

      /** optional per-element multiplier AFTER matching figma size */
      scale?: number;

      /** optional nudges in figma pixels (pre-designRoot scale) */
      nudgeX?: number;
      nudgeY?: number;
    };

    /**
     * Place object by slot and optionally apply slot.scale.
     * - fitToSize true means: match the slot's w/h first
     * - then apply slot.scale multiplier (1 = no change)
     */
    function placeFromSlot(
      obj: PIXI.Container,
      slot: FigmaSlot,
      opts?: { fitToSize?: boolean }
    ) {
      const fitToSize = opts?.fitToSize ?? true;

      // Reset scale so repeated layout calls don't compound scale
      obj.scale.set(1);

      placeFromFigmaLTWH(
        obj,
        slot.left,
        slot.top,
        slot.w,
        slot.h,
        {
          fitToSize,
          nudgeX: slot.nudgeX ?? 0,
          nudgeY: slot.nudgeY ?? 0,
        }
      );

      // Apply optional multiplier AFTER the size-fit
      const s = slot.scale ?? 1;
      obj.scale.set(obj.scale.x * s, obj.scale.y * s);
    }



    function applyLandscapeLayout() {
      const screenW = app.renderer.screen.width;
      const screenH = app.renderer.screen.height;

      layoutDesignRoot(designRoot, screenW, screenH, FIGMA);

      placeFromSlot(gameBoard as any, FIGMA.GAMEBOARD);
      placeFromSlot(LogoAnim as any, FIGMA.LOGO);
      placeFromSlot(OptionsIcon as any, FIGMA.HAMBURG);

      placeFromSlot(infoButton as any, FIGMA.HOW2PLAY);
      placeFromSlot(prizeChartButton as any, FIGMA.PRIZECHARTS);
      placeFromSlot(totalwinContainer as any, FIGMA.TOTALWIN);
      placeFromSlot(BetAreaContainer as any, FIGMA.BETAMOUNT);
      placeFromSlot(StartBut as any, FIGMA.PLAY);
      placeFromSlot(autoplay as any, FIGMA.AUTOPLAY);
      placeFromSlot(BottomWin as any, FIGMA.BOTTOMWIN, { fitToSize: true });


      const slotRect = boundsFromSlots([FIGMA.CARD_LEFT, FIGMA.CARD_CENTER, FIGMA.CARD_RIGHT]);
      refitCardsToSlot(cardscreen, slotRect);
    }



    function applyPortraitLayout() {
      const screenW = app.renderer.screen.width;
      const screenH = app.renderer.screen.height;

      layoutDesignRoot(designRoot, screenW, screenH, FIGMA_P);

      placeFromSlot(gameBoard as any, FIGMA_P.GAMEBOARD);
      placeFromSlot(LogoAnim as any, FIGMA_P.LOGO);
      placeFromSlot(OptionsIcon as any, FIGMA_P.HAMBURG);

      placeFromSlot(infoButton as any, FIGMA_P.HOW2PLAY);
      placeFromSlot(prizeChartButton as any, FIGMA_P.PRIZECHARTS);
      placeFromSlot(totalwinContainer as any, FIGMA_P.TOTALWIN);
      placeFromSlot(BetAreaContainer as any, FIGMA_P.BETAMOUNT);
      placeFromSlot(StartBut as any, FIGMA_P.PLAY);
      placeFromSlot(autoplay as any, FIGMA_P.AUTOPLAY);
      placeFromSlot(BottomWin as any, FIGMA_P.BOTTOMWIN, { fitToSize: true });


      const slotRect = boundsFromSlots([FIGMA_P.CARD_LEFT, FIGMA_P.CARD_CENTER, FIGMA_P.CARD_RIGHT]);
      refitCardsToSlot(cardscreen, slotRect);
    }





    /**
     * Adjusts layout for landscape orientation
     * Sets positions and scales for all game elements in landscape mode
     */
    landScapeAdjustFun = () => {
      if (portrait === true) {
        Landscape = true;
        portrait = false;
      }

      applyLandscapeLayout();

      // Things that are NOT part of the figma/board layout can remain screen-based:


      // Cardscreen / start/end pages are gameplay systems; we can refactor them next
    };


    /**
     * Adjusts layout for portrait orientation
     * Sets positions and scales for all game elements in portrait mode
     */
    PortraitAdjustFun = () => {
      applyPortraitLayout();
    };

    let lastIsPortrait: boolean | null = null;

    function applyResponsiveLayout() {
      const screenW = app.renderer.screen.width;
      const screenH = app.renderer.screen.height;
      const isPortraitNow = screenH > screenW;

      // Always reapply layout (or only when it changes)
      lastIsPortrait = isPortraitNow;

      if (isPortraitNow) {
        applyPortraitLayout();
      } else {
        applyLandscapeLayout();
      }
    }

    // run once immediately
    applyResponsiveLayout();

    // run on resize/orientation changes
    window.addEventListener("resize", applyResponsiveLayout);
    window.addEventListener("orientationchange", applyResponsiveLayout);


    // Initial orientation check and setup
    if (window.matchMedia("(orientation:portrait)").matches) {
      portrait = true;
      Landscape = false;
      PortraitAdjustFun();
    } else {
      portrait = false;
      Landscape = true;
      landScapeAdjustFun();
    }
  }
}

/**
 * Exports for layout adjustment functions
 */
export {
  PortraitAdjustFun,
  landScapeAdjustFun,
  PortraitAdjust_preloader,
  landscapeAdjust_preloader,
};

app_main();
